import {Action, handleActions} from 'redux-actions';
import {Company, CompanyStatus, CompanyVotesByDate} from '../types/Company';
import Event from '../actions/event';
import {
    BasicCompanyDataUpdatedPayload,
    CompanyBookingAddedPayload,
    CompanyCreatedPayload,
    CompanyEmployeeAssignedPayload, CompanyEmployeeUnassignedPayload,
    CompanyListFetchedPayload,
    CompanySelectedPayload, CompanyStatusListFetchedPayload, CompanyTesterRemovedPayload, CompanyTesterSetPayload,
    CompanyVotesByDateFetchedPayload,
} from '../actions/companyEvent';

export interface CompanyState {
    companyList: Company[];
    companyStatusMap: Record<string, CompanyStatus>;
    companyVotesByDateMap: Record<string, CompanyVotesByDate>;
    selectedCompanyId: string|null;
}

export const companyInitialState: CompanyState = {
    companyList: [],
    companyStatusMap: {},
    companyVotesByDateMap: {},
    selectedCompanyId: null,
};

/* eslint-disable max-len */
export const companyReducer = handleActions<CompanyState, any>(
    {
        [Event.Company.companyListFetched.toString()]: (state, action: Action<CompanyListFetchedPayload>): CompanyState => {
            return {
                ...state,
                companyList: action.payload.companyList,
            };
        },
        [Event.Company.companyStatusListFetched.toString()]: (state, action: Action<CompanyStatusListFetchedPayload>): CompanyState => {
            const companyStatusMap: Record<string, CompanyStatus> = {};

            action.payload.companyStatusList.forEach(companyStatus => {
                companyStatusMap[companyStatus.companyId] = companyStatus;
            });

            return {
                ...state,
                companyStatusMap,
            };
        },
        [Event.Company.companyCreated.toString()]: (state, action: Action<CompanyCreatedPayload>): CompanyState => {
            return {
                ...state,
                companyList: [
                    ...state.companyList,
                    action.payload.company,
                ],
            };
        },
        [Event.Company.companySelected.toString()]: (state, action: Action<CompanySelectedPayload>): CompanyState => {
            return {
                ...state,
                selectedCompanyId: action.payload.companyId,
            };
        },
        [Event.Company.companyEmployeeAssigned.toString()]: (state, action: Action<CompanyEmployeeAssignedPayload>): CompanyState => {
            return {
                ...state,
                companyList: state.companyList.map(company => {
                    if (company.id !== action.payload.companyId) {
                        return company;
                    }

                    return {
                        ...company,
                        employees: [
                            ...company.employees,
                            action.payload.userId,
                        ],
                    };
                }),
            };
        },
        [Event.Company.companyEmployeeUnassigned.toString()]: (state, action: Action<CompanyEmployeeUnassignedPayload>): CompanyState => {
            return {
                ...state,
                companyList: state.companyList.map(company => {
                    if (company.id !== action.payload.companyId) {
                        return company;
                    }

                    return {
                        ...company,
                        employees: company.employees.filter(employee => employee !== action.payload.userId),
                    };
                }),
            };
        },
        [Event.Company.companyTesterSet.toString()]: (state, action: Action<CompanyTesterSetPayload>): CompanyState => {
            return {
                ...state,
                companyList: state.companyList.map(company => {
                    if (company.id !== action.payload.companyId) {
                        return company;
                    }

                    return {
                        ...company,
                        tester: action.payload.userId,
                    };
                }),
            };
        },
        [Event.Company.companyTesterRemoved.toString()]: (state, action: Action<CompanyTesterRemovedPayload>): CompanyState => {
            return {
                ...state,
                companyList: state.companyList.map(company => {
                    if (company.id !== action.payload.companyId) {
                        return company;
                    }

                    return {
                        ...company,
                        tester: null,
                    };
                }),
            };
        },
        [Event.Company.basicCompanyDataUpdated.toString()]: (state, action: Action<BasicCompanyDataUpdatedPayload>): CompanyState => {
            return {
                ...state,
                companyList: state.companyList.map(company => {
                    if (company.id !== action.payload.companyId) {
                        return company;
                    }

                    const { companyId: _1, companyName, ...dataToUpdate } = action.payload;

                    return {
                        ...company,
                        ...dataToUpdate,
                        name: companyName,
                    };
                }),
            };
        },
        [Event.Company.companyBookingAdded.toString()]: (state, action: Action<CompanyBookingAddedPayload>): CompanyState => {
            return {
                ...state,
                companyList: state.companyList.map(company => {
                    if (company.id !== action.payload.companyId) {
                        return company;
                    }
                    // Only update bookings map — flat fields stay frozen from original registration
                    return {
                        ...company,
                        bookings: {
                            ...company.bookings,
                            [action.payload.version]: {
                                qualityCheck: action.payload.qualityCheck,
                                qualityCheckDate: action.payload.qualityCheckDate,
                                marketing: action.payload.marketing,
                                marketingDate: action.payload.marketingDate,
                            },
                        },
                    };
                }),
            };
        },
        [Event.Company.companyVotesByDateFetched.toString()]: (state, action: Action<CompanyVotesByDateFetchedPayload>): CompanyState => {
            return {
                ...state,
                companyVotesByDateMap: { ...action.payload.companyVotesMap },
            };
        },
    },
    companyInitialState,
);
